import { createFileRoute, Link } from "@tanstack/react-router";
import { Rocket } from "lucide-react";

export const Route = createFileRoute("/termsofpurchase")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Terms of Purchase — Three Day Launch" },
      { name: "description", content: "Terms of Purchase for the Three Day Launch Website Special + Website Concierge offer." },
      { property: "og:title", content: "Terms of Purchase — Three Day Launch" },
      { property: "og:description", content: "Terms of Purchase for the Three Day Launch Website Special + Website Concierge offer." },
      { property: "og:url", content: "https://threedaylaunch.com/termsofpurchase" },
    ],
    links: [{ rel: "canonical", href: "https://threedaylaunch.com/termsofpurchase" }],
  }),
  component: TermsOfPurchasePage,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-2xl font-semibold mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function TermsOfPurchasePage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="mx-auto max-w-4xl px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary-gradient flex items-center justify-center">
              <Rocket className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold">Three Day Launch<sup className="text-[0.6em] ml-0.5">®</sup></span>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Back home</Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
          Terms of Purchase
        </h1>

        <div className="mt-10 space-y-10 text-foreground/90 leading-relaxed">
          <p>
            By purchasing the Three Day Launch Website Special + Website Concierge offer (“Offer”), you (“Client”) agree to the following Terms of Purchase with Three Day Launch / Sierra Lin Design (“Freelancer”).
          </p>

          <Section title="1. Offer Overview">
            <p>
              This Offer includes the design and build of a custom website with up to four (4) main website pages. Standard page examples may include Home, About, Services, Contact, or similar core business pages.
            </p>
            <p>
              This Offer may also include must-have back-end website pages, such as a Blog page, 404 page, Privacy Policy page, and Terms and Conditions page. Client understands that Freelancer does not provide legal advice or legal policy drafting. Client is responsible for providing, reviewing, and approving all legal policy content, including Privacy Policy, Terms and Conditions, disclaimers, accessibility statements, cookie notices, and any other legal website content.
            </p>
            <p>
              The website design includes mobile-responsive design, basic SEO-friendly setup, and connection to Client’s preferred email marketing platform where technically available. “SEO-friendly setup” means basic on-page and technical best-practice setup; it does not guarantee search rankings, traffic, AI search visibility, leads, sales, or conversions.
            </p>
            <p>
              Final page structure, platform, design direction, and implementation approach are determined by Freelancer based on the selected package, Client’s business needs, project timeline, and materials provided by Client.
            </p>
            <p>
              Any pages, functionality, copywriting, integrations, automations, funnels, e-commerce features, course setup, membership setup, custom code, advanced SEO, advanced accessibility work, database functionality, or other work not specifically listed in this Offer is excluded and may require an additional fee.
            </p>
          </Section>

          <Section title="2. Payment Terms and Website Concierge Commitment">
            <p>
              For this Offer, the initial custom website design fee is promotional and may be listed at a discounted promotional amount at checkout.
            </p>
            <p>
              Client also agrees to the Website Concierge recurring service connected to this Offer. Website Concierge is included with and required for this Offer. This Offer is not a standalone website design purchase.
            </p>
            <p>
              Website Concierge is billed at the amount and frequency shown at checkout, unless otherwise agreed in writing.
            </p>
            <p>
              Client agrees to a minimum Website Concierge commitment of six (6) recurring billing cycles, totaling approximately twenty-four (24) weeks. During the minimum commitment period, Client is responsible for all recurring payments, even if Client delays the project, chooses not to use the website, requests cancellation, becomes unresponsive, or no longer wants the services.
            </p>
            <p>
              After the minimum commitment has been completed, Client may cancel Website Concierge by providing written notice at least thirty (30) days before the next billing date. Cancellation requests must be sent to hello@threedaylaunch.com.
            </p>
            <p>
              Cancellation stops future recurring billing only and does not create a refund for amounts already paid or amounts due during the minimum commitment period.
            </p>
            <p>
              Client authorizes Freelancer and/or its payment processor to charge the payment method provided at checkout for all amounts due under this Offer. Failed or late payments may result in delayed work, paused service, suspension of hosting/support, delayed launch, or withholding of final delivery, offboarding, transfer, export, or access until the account is brought current.
            </p>
          </Section>

          <Section title="3. No Refund Policy">
            <p>
              All sales are final. Due to the promotional website pricing, reserved project time, digital service commencement, strategy, preparation, and custom design work involved, refunds are not provided except where required by law.
            </p>
            <p>
              Cancellation, project delay, lack of response, missed deadlines, failure to provide materials, failed payment, or Client’s decision not to move forward does not cancel payment obligations already incurred under this Offer.
            </p>
          </Section>

          <Section title="4. Domain Registration and Third-Party Costs">
            <p>
              Domain registration, renewals, transfers, DNS fees, email hosting, paid plugins, premium fonts, stock assets, third-party apps, software subscriptions, and related third-party costs are not included unless expressly stated in writing.
            </p>
            <p>
              Client is solely responsible for maintaining ownership, payment, access, and account standing for their domain, email platform, payment processor, email marketing platform, and any third-party tools connected to the website.
            </p>
            <p>
              Client should maintain ownership of their domain through their own domain registrar account unless otherwise agreed in writing. Freelancer is not responsible for domain ownership disputes, domain expiration, registrar issues, DNS delays, or loss of domain access.
            </p>
            <p>
              Freelancer is not responsible for downtime, outages, bugs, account restrictions, platform limitations, billing issues, plugin conflicts, deliverability issues, registrar issues, policy changes, security incidents, data loss, or service interruptions caused by third-party platforms or providers.
            </p>
          </Section>

          <Section title="5. Website Concierge Services, Hosting, and Account Management">
            <p>
              Website Concierge is included with and required for this Offer. Client understands that the promotional website design fee is available because Client is also enrolling in Website Concierge for the required minimum commitment stated in Section 2.
            </p>
            <p>
              During an active Website Concierge subscription, Freelancer will host and manage Client’s website through Freelancer’s Lovable account, workspace, or related hosting setup. Hosting may include publication of the website, SSL support, connection to Client’s custom domain, and reasonable platform maintenance related to keeping the website available through the hosting environment.
            </p>
            <p>
              Client understands that the website may be built, hosted, edited, and maintained inside Freelancer’s Lovable account or workspace. Client does not receive owner, admin, billing, or direct workspace access to Freelancer’s Lovable account unless Freelancer expressly agrees in writing.
            </p>
            <p>
              Client understands that website edits and updates will be handled through Freelancer’s request process rather than through direct access to Freelancer’s Lovable account or workspace.
            </p>
            <p>
              Client remains responsible for owning and maintaining their business domain name, DNS access, email platform, email marketing platform, payment processor, legal policies, and any third-party accounts connected to the website. Freelancer is not responsible for domain registration, domain renewal, email hosting, email deliverability, third-party platform fees, or outside software subscriptions unless expressly stated in writing.
            </p>
            <p>
              Website Concierge includes the services listed at checkout, which may include limited website edits, one SEO-focused blog post per month, and/or an SEO Mapping Content Plan, depending on the package purchased and the offer details shown at checkout.
            </p>
            <p>
              “Up to 4 website edits per month” means up to four reasonable content or image updates to existing website pages per billing period. Website edits do not include advanced integrations, SEO audits, course setup, membership setup, e-commerce setup, emergency support, custom development, new features, or troubleshooting unrelated to Freelancer’s work unless otherwise agreed in writing.
            </p>
            <p>
              Monthly edits do not roll over if unused. Requests must be submitted through Freelancer’s preferred request process. Freelancer may decline, rescope, delay, or quote separately any request that exceeds the included support.
            </p>
            <p>
              “One SEO-focused blog post per month” means one blog post drafted and/or prepared based on Client’s business, offers, audience, keywords, and available information. Client is responsible for reviewing and approving all blog content for accuracy, legal compliance, professional claims, industry requirements, and brand alignment before publication.
            </p>
            <p>
              Client is responsible for keeping their own copies of important business materials, including website copy, brand assets, images, legal policies, blog content, lead magnets, customer data, and third-party platform data. Freelancer does not guarantee permanent backup, storage, or recovery of Client materials.
            </p>
            <p>
              Freelancer does not guarantee that hosting, blog posts, SEO setup, website edits, or content recommendations will produce specific rankings, traffic, leads, sales, revenue, conversions, or visibility.
            </p>
          </Section>

          <Section title="6. Hosting Access, Cancellation, and Website Offboarding">
            <p>
              Hosting through Freelancer’s Lovable account or workspace is included only while Client’s Website Concierge subscription is active and in good standing.
            </p>
            <p>
              If Client fails to pay, initiates a chargeback, cancels after the minimum commitment, or otherwise ends Website Concierge, Freelancer may pause website edits, suspend support, remove access to included services, disconnect the website from active hosting, unpublish the website, or begin offboarding after reasonable notice, except where prohibited by law.
            </p>
            <p>
              If Website Concierge ends for any reason, Freelancer may provide Client with up to fourteen (14) days to request offboarding or arrange continued hosting, provided Client’s account is paid in full and in good standing.
            </p>
            <p>
              After Client has completed the minimum Website Concierge commitment and paid all amounts due, Client may request website offboarding. Offboarding may include one of the following, depending on what is technically available through the platform at that time:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>transferring or duplicating the project into Client’s own account or workspace, if supported;</li>
              <li>providing reasonable migration instructions or coordination with Client’s new provider.</li>
            </ul>
            <p>
              Offboarding, transfer, export, migration, DNS changes, GitHub setup, external deployment, or support for another provider is not included unless expressly stated in writing. These services may require an additional fee.
            </p>
            <p>
              Client understands that some website functionality, design settings, hosting behavior, integrations, forms, databases, platform features, or AI-assisted tools may depend on Lovable, Freelancer’s workspace setup, or other third-party tools. Freelancer does not guarantee that the website can be transferred, exported, or recreated on another platform with identical functionality, appearance, performance, or ease of editing.
            </p>
            <p>
              Client is responsible for securing their own hosting, platform subscription, developer support, domain access, DNS management, email records, third-party integrations, and ongoing maintenance if they choose to leave Website Concierge.
            </p>
            <p>
              Client’s domain, logo, brand assets, written content, and Client-provided materials remain Client’s property. Freelancer’s Lovable account, workspace, internal systems, processes, templates, design methods, reusable components, workflows, and account-level settings remain Freelancer’s property.
            </p>
          </Section>

          <Section title="7. Client Responsibilities and Project Readiness">
            <p>
              Client agrees to provide all requested questionnaires, content, images, brand materials, logins, domain access, email platform access, approvals, feedback, and communication by the deadlines provided.
            </p>
            <p>
              Client’s project questionnaire and required prep materials must be completed at least seven (7) days before the scheduled kickoff date. If Client does not complete the required prep materials by the deadline, Freelancer may reschedule the project to the next available opening.
            </p>
            <p>
              The 3-day project timeline begins only after Client has provided all required materials, access, approvals, and payments. If Client delays, misses deadlines, provides incomplete materials, requests major changes, or is unavailable for required feedback, the timeline may be extended, paused, rescheduled, or converted into a standard project timeline at Freelancer’s discretion.
            </p>
            <p>
              Client must be available by email or the agreed communication method during the build window to answer questions, provide feedback, and approve work. Freelancer is not responsible for missed launch dates caused by Client delay, missing information, lack of access, late approvals, third-party issues, domain/DNS issues, payment issues, or changes in scope.
            </p>
          </Section>

          <Section title="8. Launch and Delivery">
            <p>
              Freelancer will make reasonable efforts to launch the website on Day 3 of the scheduled build window when Client has provided all required materials, access, approvals, and payment.
            </p>
            <p>
              If the website cannot be launched due to Client delay, missing approvals, missing domain access, incomplete content, legal policy delays, third-party issues, or other circumstances outside Freelancer’s control, the project may be considered delivered as a completed website draft or staged website. Additional launch support may be completed through Website Concierge or quoted separately.
            </p>
            <p>
              Client understands that delivery may mean publication of the website through Freelancer’s managed hosting environment, completion of the staged website, or launch of the website on Client’s custom domain, depending on project status and Client readiness.
            </p>
            <p>
              Because the website may be hosted inside Freelancer’s Lovable account or workspace, delivery does not automatically include admin access, owner access, account credentials, source files, export files, transfer rights, or access to Freelancer’s Lovable account.
            </p>
            <p>
              Freelancer may withhold launch, hosting, support, offboarding, export, transfer, credentials, or final access until Client has completed all required payments and Client’s account is current.
            </p>
          </Section>

          <Section title="9. Revision Limits">
            <p>
              This Offer includes up to three (3) rounds of revisions during the active project window, unless otherwise stated in writing.
            </p>
            <p>
              A revision round means one consolidated set of Client feedback submitted at one time. Client agrees to gather all feedback, edits, and requested changes into a single organized response for each revision round.
            </p>
            <p>
              Revision rounds may include reasonable adjustments to existing design, layout, copy placement, images, or page content. Revision rounds do not include new pages, new functionality, changes to previously approved direction, full redesigns, platform changes, or requests outside the original scope.
            </p>
            <p>
              Unused revision rounds expire at launch or within one (1) day of delivery, whichever comes first. Additional revisions may be billed separately or completed through Website Concierge if they fall within the included monthly support.
            </p>
          </Section>

          <Section title="10. Attribution Requirement">
            <p>
              Client agrees that the completed website may include a visible footer credit or similar acknowledgment stating that the website was designed by Three Day Launch, unless otherwise agreed in writing.
            </p>
          </Section>

          <Section title="11. No Guarantee of Results">
            <p>
              Client acknowledges that Freelancer cannot guarantee business results, sales, traffic, leads, SEO rankings, AI search visibility, conversions, revenue, or website performance.
            </p>
            <p>
              Results depend on many factors outside Freelancer’s control, including Client’s offer, audience, pricing, market demand, follow-up, advertising, search engine updates, competition, ongoing content creation, and business operations.
            </p>
          </Section>

          <Section title="12. Confidentiality">
            <p>
              Freelancer may receive confidential business information from Client while performing services. Freelancer agrees not to disclose or improperly use Client confidential information except as necessary to perform the services, operate the project, use approved subcontractors/tools, comply with law, or as authorized by Client.
            </p>
            <p>
              Client also agrees not to disclose Freelancer’s proprietary systems, methods, pricing structures, processes, templates, workflows, code structures, strategies, internal business materials, or account-level setup.
            </p>
          </Section>

          <Section title="13. Publicity and Portfolio Rights">
            <p>
              Unless Client requests confidentiality in writing before project launch, Client agrees Freelancer may use Client’s business name, logo, publicly available brand materials, and non-confidential completed project work for portfolio, website, advertising, promotional content, social media, case studies, awards, blogs, and similar purposes.
            </p>
            <p>
              Freelancer will not knowingly disclose Client’s confidential information, private customer information, unpublished proprietary materials, revenue data, or sensitive internal business information without written permission.
            </p>
          </Section>

          <Section title="14. Reuse of Design Elements">
            <p>
              Client acknowledges that Freelancer retains ownership of general design methods, systems, frameworks, code structures, templates, layouts, concepts, workflows, processes, and non-exclusive creative elements used in the project.
            </p>
            <p>
              Freelancer may reuse, adapt, or incorporate such elements into future projects, provided Client’s confidential information, proprietary content, customer information, or unique branding assets are not reused without consent.
            </p>
          </Section>

          <Section title="15. Client Content, Claims, and Legal Compliance">
            <p>
              Client is solely responsible for the accuracy, legality, ownership, and appropriateness of all content, claims, images, testimonials, offers, pricing, policies, forms, downloads, email opt-ins, and materials provided to Freelancer or published on Client’s website.
            </p>
            <p>
              Client agrees not to provide materials that infringe another party’s copyright, trademark, privacy rights, publicity rights, or other rights.
            </p>
            <p>
              Client is responsible for ensuring that their website content, offers, policies, disclaimers, accessibility requirements, privacy practices, email marketing practices, testimonials, professional claims, income claims, health claims, financial claims, and business operations comply with applicable laws and industry requirements.
            </p>
            <p>
              Freelancer does not provide legal, financial, medical, tax, accessibility, privacy, or compliance advice.
            </p>
          </Section>

          <Section title="16. Acceptable Use of Hosted Website">
            <p>
              Because Client’s website may be hosted through Freelancer’s Lovable account, workspace, or related hosting setup, Client agrees not to use the website for unlawful, harmful, deceptive, infringing, abusive, spam-related, adult, hateful, violent, regulated, or otherwise high-risk content or activity.
            </p>
            <p>
              Client agrees not to publish or request publication of content, offers, claims, forms, downloads, images, testimonials, automations, scripts, or integrations that violate applicable laws, third-party rights, platform rules, payment processor rules, email marketing rules, privacy requirements, or the terms of any third-party service used to host, operate, or connect the website.
            </p>
            <p>
              Freelancer may refuse to publish, remove, disable, suspend, or request revisions to any content or functionality that Freelancer reasonably believes could create legal, platform, reputational, security, compliance, or account risk.
            </p>
            <p>
              Client remains solely responsible for the legality, accuracy, ownership, compliance, and business use of all website content, offers, claims, forms, policies, and materials.
            </p>
          </Section>

          <Section title="17. Indemnification">
            <p>
              Client agrees to defend, release, indemnify, and hold harmless Freelancer and its owners, employees, contractors, representatives, successors, and assigns from claims, damages, liabilities, costs, and expenses, including reasonable attorneys’ fees, arising out of Client-provided content, Client’s business operations, Client’s website claims, products or services sold by Client, copyright or trademark infringement in Client materials, privacy or compliance issues related to Client’s business, Client’s misuse of the website, Client’s violation of platform or third-party terms, or Client’s negligence or misconduct, except to the extent caused by Freelancer’s gross negligence or willful misconduct.
            </p>
          </Section>

          <Section title="18. Limitation of Liability">
            <p>
              Except as expressly stated in this Agreement, Freelancer makes no warranties, express or implied.
            </p>
            <p>
              Freelancer shall not be liable for indirect, incidental, special, consequential, punitive, lost profit, lost revenue, lost data, lost opportunity, or business interruption damages.
            </p>
            <p>
              Freelancer’s total liability for any claim shall not exceed the amount actually paid by Client to Freelancer for the specific service giving rise to the claim.
            </p>
          </Section>

          <Section title="19. Chargebacks and Payment Disputes">
            <p>
              Client agrees to contact Freelancer in writing before initiating a chargeback, payment dispute, or processor claim so the parties can attempt to resolve the concern directly.
            </p>
            <p>
              If Client initiates a chargeback for amounts validly owed under this Agreement, Freelancer may pause work, suspend services, withhold delivery, disable support, remove active hosting, or pursue collection of outstanding amounts, including reasonable costs and fees where permitted by law.
            </p>
          </Section>

          <Section title="20. Independent Contractor">
            <p>
              Freelancer is an independent contractor and not an employee, partner, joint venturer, or agent of Client.
            </p>
            <p>
              Nothing in this Agreement authorizes Freelancer to bind Client to contracts, represent Client legally, or act as Client’s employee without written consent.
            </p>
          </Section>

          <Section title="21. Contractor and Team Support">
            <p>
              Freelancer may use employees, contractors, subcontractors, consultants, designers, developers, copywriters, assistants, specialists, or other team members to help perform services under this Agreement.
            </p>
            <p>
              Freelancer remains responsible for managing the work performed by its team and contractors. Client understands that not every task will be performed personally by Freelancer.
            </p>
            <p>
              Freelancer will take reasonable steps to ensure any team member or contractor with access to Client confidential information uses that information only as needed to perform the services.
            </p>
          </Section>

          <Section title="22. Force Majeure">
            <p>
              Freelancer shall not be liable for delay or failure in performance caused by events beyond reasonable control, including but not limited to illness, emergency, fire, flood, severe weather, accident, power outage, telecommunications failure, internet/network failure, acts of God, platform outages, terrorism, civil unrest, labor disputes, or other circumstances beyond Freelancer’s reasonable control.
            </p>
            <p>
              If Freelancer becomes unable to perform due to sudden illness, incapacity, death, emergency, or other unforeseeable circumstance, Freelancer may provide a replacement service provider subject to Client approval, reschedule the project, or refund payments made for unperformed services.
            </p>
          </Section>

          <Section title="23. Governing Law">
            <p>
              This Agreement shall be governed by the laws of the State of Illinois, without regard to conflict of law principles. Any dispute arising under this Agreement shall be handled in the state or federal courts located in Illinois, unless otherwise required by law.
            </p>
          </Section>

          <Section title="24. Severability and Waiver">
            <p>
              If any provision of this Agreement is found unenforceable, the remaining provisions remain in full force and effect.
            </p>
            <p>
              Failure to enforce any provision shall not constitute waiver of future enforcement rights.
            </p>
          </Section>

          <Section title="25. Acceptance of Terms">
            <p>
              By purchasing this Offer, checking the agreement box at checkout, submitting payment information, or beginning the project, Client confirms they have read, understood, and agreed to these Terms of Purchase in full.
            </p>
          </Section>
        </div>
      </main>
    </div>
  );
}
